import React, { FC, useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  Avatar,
  ListItemText,
  CircularProgress,
  Box,
  Typography,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { useUsersService } from '../../../../../../apis/users/useUsersService';
import { TPrimaryDetails } from '../../../../../../apis/users/users.interfaces';
import { useMessagingContext } from '../../../contexts/messaging.context';

type NewConversationDialogProps = {
  open: boolean;
  onClose: () => void;
};

const DEBOUNCE_MS = 300;

const NewConversationDialog: FC<NewConversationDialogProps> = ({
  open,
  onClose,
}) => {
  const { translateText } = useTranslation();
  const { searchUsers } = useUsersService();
  const {
    setSelectedReceiver,
    setSelectedConversation,
    setSelectedClass,
    setPendingNewConversation,
  } = useMessagingContext();

  const [query, setQuery] = useState('');
  const [users, setUsers] = useState<TPrimaryDetails[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchUsers = useCallback(async () => {
    const q = query.trim();
    if (!q) {
      setUsers([]);
      return;
    }
    setLoading(true);
    try {
      const res = await searchUsers(q);
      setUsers(res.data || []);
    } catch {
      setUsers([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(fetchUsers, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, open, fetchUsers]);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setUsers([]);
    }
  }, [open]);

  const handleSelectUser = (user: TPrimaryDetails) => {
    const userId = user.id;
    if (!userId) return;
    setPendingNewConversation({
      _id: userId,
      first_name: user.firstName,
      last_name: user.lastName,
      email: '',
      profile_picture: user.profile?.profilePicture?.fileUrl,
    });
    setSelectedReceiver(userId);
    setSelectedConversation(`pending-${userId}`);
    setSelectedClass(null);
    onClose();
  };

  const getDisplayName = (u: TPrimaryDetails) =>
    [u.firstName, u.middleName, u.lastName].filter(Boolean).join(' ');

  const getAvatarUrl = (u: TPrimaryDetails) =>
    u.profile?.profilePicture?.fileUrl || '';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
      data-testid="new-conversation-dialog"
    >
      <DialogTitle data-testid="new-conversation-dialog-title">
        {translateText('communications.messaging.startNewConversation')}
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        <Box sx={{ my: 1 }}>
          <TextField
            fullWidth
            size="small"
            placeholder={translateText(
              'communications.messaging.searchNameOrId'
            )}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
            data-testid="new-conversation-search"
          />
        </Box>
        <Box sx={{ minHeight: 200, maxHeight: 320, overflow: 'auto' }}>
          {loading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                py: 4,
              }}
            >
              <CircularProgress size={24} />
            </Box>
          ) : !query.trim() ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ py: 3, textAlign: 'center' }}
            >
              {translateText('communications.messaging.searchNameOrId')}
            </Typography>
          ) : users.length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ py: 3, textAlign: 'center' }}
            >
              {translateText('communications.messaging.noUsersFound')}
            </Typography>
          ) : (
            <List dense disablePadding>
              {users.map((user) => (
                <ListItem key={user.id || ''} disablePadding>
                  <ListItemButton
                    onClick={() => handleSelectUser(user)}
                    data-testid={`new-conversation-user-${user.id}`}
                  >
                    <ListItemAvatar>
                      <Avatar src={getAvatarUrl(user)}>
                        {getDisplayName(user).charAt(0).toUpperCase()}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={getDisplayName(user)}
                      secondary={user.id}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default NewConversationDialog;
